import { useQuery } from "@tanstack/react-query";
import {
  authHeaders, getStructureOccupancy, getStructureSummary, getWarehousePlan, listExpectedStockSnapshots,
  listInventoryRuns, listZones,
} from "../api/rest";
import { drawablePolygons } from "../pages/zonesPlan";

/**
 * What the active warehouse actually HAS, as booleans (ADR 227).
 *
 * The layer panel used to offer a checkbox per layer CLASS and let each layer's
 * renderer decide it had nothing to draw. That reads as a broken product: on a
 * freshly imported warehouse "Paletes e cargas" and the whole KPI section were
 * switched on and did nothing, and "Planta 2D" was offered for a plan nobody had
 * uploaded. The same objection as the ADR 224 badge — a control bound to nothing
 * is a claim the app cannot keep.
 *
 * Every signal here is a COUNT or a metadata probe, never the payload itself: the
 * rack layer's own read is ~15k prisms, far too heavy to fetch just to decide
 * whether to draw a checkbox.
 *
 * While a signal is still loading its flag is `false` — the row appears when the
 * data is confirmed, so the panel grows rather than flickering rows away.
 */
export interface LiveDataAvailability {
  /** Rack prisms exist (structure derived AND levels resolved). */
  racks: boolean;
  /** At least one paintable inventory run for this warehouse. */
  runs: boolean;
  /** A 2D plan can be drawn: an uploaded plan, the archived SLAM map, or (ADR 303)
   *  the warehouse's own zone footprints. */
  plan: boolean;
  /** At least one zone is defined for this warehouse. */
  zones: boolean;
  /** A WMS snapshot was imported for this warehouse — the only thing an accuracy
   *  figure can be computed against (ADR 410 rule: no WMS, no card, no badge). */
  wms: boolean;
  /** The newest paintable run measured at least one slot's fill height, so a
   *  vertical-occupancy figure exists somewhere (ADR 292). */
  vertical: boolean;
  /** True while any probe is still in flight — lets callers hold a skeleton. */
  loading: boolean;
}

/** Does a 2D raster exist for this warehouse? Uploaded plan first (ADR 215), then
 *  the archived SLAM raster — the same precedence the Minimap itself uses, so the
 *  checkbox and the widget can never disagree. Only the yaml is probed: it is a few
 *  hundred bytes, while the .pgm is megabytes. */
async function hasRaster(siteId: string, warehouseId: string): Promise<boolean> {
  try {
    if (await getWarehousePlan(siteId, warehouseId)) return true;
  } catch {
    /* unreadable plan metadata: fall through to the SLAM probe */
  }
  try {
    const res = await fetch(`/v1/models/warehouses/${encodeURIComponent(warehouseId)}/map.yaml`,
      { headers: authHeaders(), credentials: "include" });
    return res.ok;
  } catch {
    return false;
  }
}

export function useLiveDataAvailability(
  siteId: string, warehouseId: string | undefined,
): LiveDataAvailability {
  const enabled = !!siteId && !!warehouseId;

  const summary = useQuery({
    queryKey: ["structure-summary", siteId, warehouseId],
    queryFn: () => getStructureSummary(siteId, warehouseId!),
    enabled,
    staleTime: 60_000,
  });

  // Shares its key with RunSelectionSection's query, so the panel's two consumers
  // hit the cache once rather than fetching the run list twice.
  const runs = useQuery({
    queryKey: ["inventory-runs", siteId],
    queryFn: () => listInventoryRuns(siteId),
    enabled: !!siteId,
    staleTime: 30_000,
  });

  const zones = useQuery({
    queryKey: ["zones", siteId, warehouseId],
    queryFn: () => listZones(siteId, warehouseId),
    enabled,
    staleTime: 60_000,
  });

  const plan = useQuery({
    queryKey: ["warehouse-raster", siteId, warehouseId],
    queryFn: () => hasRaster(siteId, warehouseId!),
    enabled,
    staleTime: 60_000,
  });

  const paintable = (runs.data ?? []).filter(
    (r) => r.warehouseId === warehouseId && r.status !== "failed",
  );
  const latestRunId = paintable[0]?.id;

  // Metadata list, a few rows at most.
  const wms = useQuery({
    queryKey: ["expected-stock-snapshots", siteId, warehouseId],
    queryFn: () => listExpectedStockSnapshots(siteId, warehouseId!),
    enabled,
    staleTime: 60_000,
  });

  // depth 0 = the warehouse root alone, whose rollup already sums every slot
  // (ADR 276); the untruncated tree is megabytes.
  const vertical = useQuery({
    queryKey: ["structure-occupancy-root", siteId, warehouseId, latestRunId],
    queryFn: () => getStructureOccupancy(siteId, warehouseId!, latestRunId!, 0),
    enabled: enabled && !!latestRunId,
    staleTime: 60_000,
  });

  return {
    racks: (summary.data?.rackVolumes ?? 0) > 0,
    runs: paintable.length > 0,
    // ADR 303: zones are a plan of last resort, so the toggle appears wherever the
    // Minimap has something to draw — the same three sources in the same order.
    // The predicate must be the Minimap's OWN (a zone with a null or degenerate
    // polygon draws nothing): counting zones here would put the checkbox back on a
    // panel that renders nothing, which is the bug this whole layer is about.
    plan: plan.data === true || drawablePolygons(zones.data ?? []).length > 0,
    zones: (zones.data?.length ?? 0) > 0,
    wms: (wms.data?.length ?? 0) > 0,
    vertical: (vertical.data?.nodes ?? []).some((n) => n.occupancy.verticalMeasuredSpaces > 0),
    loading: summary.isLoading || runs.isLoading || zones.isLoading || plan.isLoading
      || wms.isLoading || vertical.isLoading,
  };
}
